import {
  Button,
  type CompanyFormat,
  formatDateTime,
  formatMoney,
  ListView,
  PageEmpty,
  PageError,
  PageHeader,
  PageLoading,
  useCursorList,
} from "@majitap/ui";
import { Alert } from "@majitap/ui/arc/alert/alert";
import { Dialog, DialogContent } from "@majitap/ui/arc/dialog/dialog";
import { HoldToConfirm } from "@majitap/ui/arc/hold-to-confirm/hold-to-confirm";
import { Input } from "@majitap/ui/arc/input/input";
import SegmentedControl from "@majitap/ui/arc/segmented-control/segmented-control";
import { Textarea } from "@majitap/ui/arc/textarea/textarea";
import { useMutation, useQuery, useQueryClient, useSuspenseQuery } from "@tanstack/react-query";
import { Link, useParams } from "@tanstack/react-router";
import { Scale } from "lucide-react";
import { type ReactNode, useState } from "react";
import { api, errorOf, ok } from "../../api";
import { useCompanyFormat } from "../../company";
import { useRecordCrumb } from "../../crumb";
import { isAmount } from "../../customers";
import { messageFor } from "../../error-copy";
import { RouterLink } from "../../router-link";
import { meQuery } from "../../session";
import {
  ENTRY_KIND,
  HOLDER,
  holderHref,
  WALLET_STATUS,
  type Wallet,
  walletActions,
  walletActionsFor,
  walletQuery,
} from "../../wallets";
import { ChoiceField } from "../shared/choice-field";
import { ActivityPanel } from "../shared/panels";
import styles from "./wallets.module.css";

/** How long an adjustment must be held: it's a permanent posting. */
export const ADJUST_HOLD_MS = 1500;

function useRefreshWallet(id: string) {
  const queryClient = useQueryClient();
  return async () => {
    await queryClient.invalidateQueries({ queryKey: ["wallet", id] });
    await queryClient.invalidateQueries({ queryKey: ["wallet-statement", id] });
    await queryClient.invalidateQueries({ queryKey: ["wallets"] });
    await queryClient.invalidateQueries({ queryKey: ["activity", "wallet", id] });
  };
}

/** One wallet (P8-16, `Console / Wallet` in the design): its figures, its statement and its controls. */
export function WalletPage(): ReactNode {
  const { walletId } = useParams({ from: "/console/wallets/$walletId" });
  const { data: me } = useSuspenseQuery(meQuery);
  const permissions = me?.permissions ?? [];
  const wallet = useQuery(walletQuery(walletId));
  useRecordCrumb(wallet.data?.holder.name);
  if (wallet.status === "pending") return <PageLoading label="Loading the wallet" />;
  if (wallet.status === "error") {
    const error = errorOf(wallet.error);
    if (error.code === "WALLET_NOT_FOUND")
      return <PageEmpty title="No such wallet" description="It may be in another company, or the link is wrong." />;
    return <PageError message={messageFor(error)} onRetry={() => wallet.refetch()} />;
  }
  return <WalletView wallet={wallet.data} permissions={permissions} />;
}

function WalletView({ wallet: w, permissions }: { wallet: Wallet; permissions: string[] }) {
  const format = useCompanyFormat();
  const can = walletActionsFor(w, permissions);
  const [dialog, setDialog] = useState<"freeze" | "unfreeze" | "limit" | null>(null);
  const [statusLabel] = WALLET_STATUS[w.status];
  return (
    <>
      <PageHeader
        title={w.holder.name || "Wallet"}
        description={`${HOLDER[w.holder_type].one} wallet · ${w.currency} · ${statusLabel.toLowerCase()}`}
        actions={
          <div className={styles.actions}>
            <RouterLink href={holderHref(w.holder)} className={styles.link}>
              Open the {HOLDER[w.holder_type].one.toLowerCase()}
            </RouterLink>
            {can.creditLimit ? (
              <Button variant="secondary" onClick={() => setDialog("limit")}>
                Set the credit limit
              </Button>
            ) : null}
            {can.freeze ? (
              <Button variant="secondary" onClick={() => setDialog("freeze")}>
                Freeze
              </Button>
            ) : null}
            {can.unfreeze ? (
              <Button variant="secondary" onClick={() => setDialog("unfreeze")}>
                Unfreeze
              </Button>
            ) : null}
          </div>
        }
      />
      {w.status === "frozen" ? (
        <Alert tone="info" title="This wallet is frozen">
          Money still comes in, but nothing can be spent or withdrawn from it until it's unfrozen.
        </Alert>
      ) : null}
      <WalletFigures wallet={w} format={format} />
      <div className={styles.columns}>
        <div className={styles.main}>
          <section className={styles.section}>
            <h2 className={styles.sectionTitle}>Statement</h2>
            <Statement wallet={w} />
          </section>
          {permissions.includes("staff.manage") ? (
            <section className={styles.section}>
              <h2 className={styles.sectionTitle}>Activity</h2>
              <ActivityPanel targetType="wallet" id={w.id} label="What changed on this wallet" />
            </section>
          ) : null}
        </div>
        <aside className={styles.side}>{can.adjust ? <AdjustPanel wallet={w} /> : null}</aside>
      </div>
      {dialog === "freeze" || dialog === "unfreeze" ? (
        <FreezeDialog wallet={w} freeze={dialog === "freeze"} onClose={() => setDialog(null)} />
      ) : null}
      {dialog === "limit" ? <CreditLimitDialog wallet={w} onClose={() => setDialog(null)} /> : null}
    </>
  );
}

/** Balance, held, credit limit and available exactly as the API sent them: nothing is worked out here. */
export function WalletFigures({
  wallet: w,
  format,
}: {
  wallet: Pick<Wallet, "balance" | "held_amount" | "credit_limit" | "available" | "currency">;
  format: Pick<CompanyFormat, "currency" | "locale">;
}) {
  const money = (v: string) => formatMoney(v, { ...format, currency: w.currency });
  return (
    <div className={styles.figures}>
      {(
        [
          ["Balance", w.balance],
          ["Held", w.held_amount],
          ["Credit limit", w.credit_limit],
          ["Available", w.available],
        ] as const
      ).map(([label, value]) => (
        <div key={label} className={styles.figure}>
          <span className={styles.muted}>{label}</span>
          <strong>{money(value)}</strong>
        </div>
      ))}
    </div>
  );
}

/** Newest first, a page at a time (the API's cursor). Amounts are signed: + in, − out. */
function Statement({ wallet: w }: { wallet: Wallet }) {
  const format = useCompanyFormat();
  const list = useCursorList({
    queryKey: ["wallet-statement", w.id],
    fetchPage: (cursor) =>
      ok(
        api.GET("/v1/admin/wallets/{id}/statement", {
          params: { path: { id: w.id }, query: { cursor, limit: 25 } },
        }),
      ),
  });
  return (
    <ListView
      caption="Statement"
      itemName={{ one: "entry", other: "entries" }}
      rows={list.rows}
      rowKey={(e) => e.id}
      status={list.status}
      errorMessage={list.error ? messageFor(errorOf(list.error)) : undefined}
      onRetry={list.refetch}
      hasMore={list.hasMore}
      loadingMore={list.loadingMore}
      onLoadMore={list.loadMore}
      filtered={false}
      empty={{ title: "No movements yet", description: "Money in and out of the wallet shows here." }}
      columns={[
        {
          key: "occurred_at",
          label: "When",
          render: (e) => <span className={styles.muted}>{formatDateTime(e.occurred_at, format)}</span>,
        },
        { key: "description", label: "What", render: (e) => e.description ?? ENTRY_KIND[e.kind] },
        {
          key: "reference",
          label: "Reference",
          render: (e) =>
            e.order_id && e.order_number ? (
              <Link to="/orders/$orderId" params={{ orderId: e.order_id }} className={styles.mono}>
                {e.delivery_number ?? e.order_number}
              </Link>
            ) : e.invoice_id ? (
              <Link to="/invoices/$invoiceId" params={{ invoiceId: e.invoice_id }} className={styles.mono}>
                Invoice
              </Link>
            ) : e.payment_id ? (
              <Link to="/payments/$paymentId" params={{ paymentId: e.payment_id }} className={styles.mono}>
                Payment
              </Link>
            ) : (
              "—"
            ),
        },
        {
          key: "amount",
          label: "Amount",
          numeric: true,
          render: (e) => (
            <span className={e.amount.startsWith("-") ? styles.out : styles.in}>
              {e.amount.startsWith("-") ? "" : "+"}
              {formatMoney(e.amount, { ...format, currency: e.currency })}
            </span>
          ),
        },
      ]}
    />
  );
}

function FreezeDialog({ wallet, freeze, onClose }: { wallet: Wallet; freeze: boolean; onClose: () => void }) {
  const refresh = useRefreshWallet(wallet.id);
  const [reason, setReason] = useState("");
  const mutation = useMutation({
    mutationFn: () => (freeze ? walletActions.freeze : walletActions.unfreeze)(wallet.id, reason.trim()),
    onSuccess: async () => {
      await refresh();
      onClose();
    },
  });
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent
        title={freeze ? `Freeze ${wallet.holder.name}'s wallet?` : `Unfreeze ${wallet.holder.name}'s wallet?`}
        description={
          freeze
            ? "Money still comes in, but nothing can be spent or withdrawn until it's unfrozen."
            : "They can spend and withdraw from it again."
        }
      >
        <div className={styles.form}>
          <Textarea label="Reason" value={reason} onChange={(e) => setReason(e.target.value)} rows={2} />
          {mutation.error ? (
            <Alert tone="danger" title="Not changed">
              {messageFor(errorOf(mutation.error))}
            </Alert>
          ) : null}
          <div className={styles.footer}>
            <Button variant="secondary" onClick={onClose}>
              Cancel
            </Button>
            <Button
              variant={freeze ? "danger" : "primary"}
              disabled={!reason.trim()}
              loading={mutation.isPending}
              onClick={() => mutation.mutate()}
            >
              {freeze ? "Freeze" : "Unfreeze"}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

/** How far below zero the wallet may go: credit for a customer or business, a cash float for a driver. */
function CreditLimitDialog({ wallet, onClose }: { wallet: Wallet; onClose: () => void }) {
  const refresh = useRefreshWallet(wallet.id);
  const [limit, setLimit] = useState(wallet.credit_limit);
  const [reason, setReason] = useState("");
  const mutation = useMutation({
    mutationFn: () => walletActions.creditLimit(wallet.id, limit.trim(), reason.trim()),
    onSuccess: async () => {
      await refresh();
      onClose();
    },
  });
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent
        title="Set the credit limit"
        description={
          wallet.holder_type === "driver"
            ? "A cash float: how far below zero the driver's wallet may go, for fuel and the like. Withdrawals never use it."
            : "How far below zero the wallet may go. It can't go below what's already drawn."
        }
      >
        <div className={styles.form}>
          <Input
            label="Credit limit"
            inputMode="decimal"
            value={limit}
            onChange={(e) => setLimit(e.target.value)}
            description="0.00 for none"
          />
          <Textarea label="Reason" value={reason} onChange={(e) => setReason(e.target.value)} rows={2} />
          {mutation.error ? (
            <Alert tone="danger" title="Not saved">
              {messageFor(errorOf(mutation.error))}
            </Alert>
          ) : null}
          <div className={styles.footer}>
            <Button variant="secondary" onClick={onClose}>
              Cancel
            </Button>
            <Button
              disabled={!isAmount(limit) || !reason.trim()}
              loading={mutation.isPending}
              onClick={() => mutation.mutate()}
            >
              Save
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

/**
 * An adjustment (`ledger.adjust`, partner and driver wallets): a permanent posting, corrected only
 * by another adjustment. A credit is money owed to them; a debit is a deduction.
 */
export function AdjustPanel({ wallet }: { wallet: Pick<Wallet, "id" | "holder_type"> }) {
  const refresh = useRefreshWallet(wallet.id);
  const [direction, setDirection] = useState<"credit" | "debit">("credit");
  const [amount, setAmount] = useState("");
  const [reason, setReason] = useState("");
  const [confirmed, setConfirmed] = useState<boolean | undefined>(undefined);
  const ready = isAmount(amount) && !/^0+(\.0+)?$/.test(amount.trim()) && reason.trim().length > 0;
  const adjust = useMutation({
    mutationFn: () => walletActions.adjust(wallet.id, { direction, amount: amount.trim(), reason: reason.trim() }),
    onSuccess: async () => {
      await refresh();
      setAmount("");
      setReason("");
      // ready for the next one; the statement shows this one
      setConfirmed(false);
    },
    onError: () => setConfirmed(false),
  });
  return (
    <section className={styles.card} aria-label="Post an adjustment">
      <h2 className={styles.sectionTitle}>Post an adjustment</h2>
      <p className={styles.muted}>A permanent posting. To correct one, post another the other way.</p>
      <ChoiceField label="This posting is">
        <SegmentedControl
          label="This posting is"
          value={direction}
          onValueChange={(v) => setDirection(v as "credit" | "debit")}
          options={[
            { value: "credit", label: "Owed to them" },
            { value: "debit", label: "A deduction" },
          ]}
        />
      </ChoiceField>
      <Input label="Amount" inputMode="decimal" value={amount} onChange={(e) => setAmount(e.target.value)} />
      <Textarea
        label="Reason"
        value={reason}
        onChange={(e) => setReason(e.target.value)}
        rows={2}
        description="It's what their statement says."
      />
      <HoldToConfirm
        label="Hold to post"
        confirmedLabel="Posted"
        tone="neutral"
        icon={<Scale size={16} strokeWidth={1.75} />}
        duration={ADJUST_HOLD_MS}
        disabled={!ready || adjust.isPending}
        confirmed={confirmed}
        onConfirm={() => {
          setConfirmed(undefined);
          adjust.mutate();
        }}
      />
      {adjust.error ? (
        <Alert tone="danger" title="Not posted">
          {messageFor(errorOf(adjust.error))}
        </Alert>
      ) : null}
    </section>
  );
}
